import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid, { StyledGrid } from 'components/layout/PraxFormGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { FormState } from 'final-form';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Button, Grid, Icon, Message } from 'semantic-ui-react';
import {
  downloadFileFromFileArchiveId,
  generate,
  searchNotifications,
  updateNotifyEmails
} from 'services/testAuthorityNotificationServices';
import { NotificationDto, NotificationRequest } from 'ts-types/api.types';
import { toIsoDateString } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';

interface Props extends MessageSourceProps, RouterProps, AuthConsumerRenderProps {
}

let initialValues: Partial<NotificationRequest> = {
  fromDate: toIsoDateString(moment()),
  toDate: toIsoDateString(moment()),
  alreadyNotified: false
};

interface NotificationFiledProps extends FormState {
  values: Partial<NotificationRequest>
}

const TestAuthorityNotificationView = (props: Props) => {

  const {getMessage} = props;

  const [cancelTokenSource /*setCancelTokenSource*/] = React.useState(axios.CancelToken.source());
  const [notifications, setNotifications] = useState([] as NotificationDto[]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [successMessage, setSuccessMessage] = useState(undefined as string | undefined);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);
  const [searchValues, setSearchValues] = useState(initialValues as Partial<NotificationRequest>);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const fetchedNotifications = await searchNotifications(searchValues, cancelTokenSource);
      setNotifications(fetchedNotifications);
    } catch (e) {
      setErrorMessage(getMessage("error.general"))
    } finally {
      setDataLoaded(true);
    }
  }

  const generateTestsExportFile = async (values: Partial<NotificationRequest>) => {
    setDataLoaded(false);

    const mandateId = props.currentUser!.userConfig.mandateId;
    const request: NotificationRequest = {
      fromDate: values.fromDate!,
      toDate: values.toDate!,
      alreadyNotified: values.alreadyNotified!
    };

    await generate(request, mandateId, cancelTokenSource)
        .then(response => {
          response.length === 0
              ? setErrorMessage(getMessage("testAuthorityNotification.noGeneratedFiles"))
              : setSuccessMessage(getMessage("testAuthorityNotification.successfullyGeneratedFiles"))
        })
        .catch(e => handleError(e.response.data))
        .finally(() => {
          searchAllNotifications(values);
        })

  }

  const searchAllNotifications = async (values: Partial<NotificationRequest>) => {
    setDataLoaded(false);

    await searchNotifications(values, cancelTokenSource)
        .then(response => {
          setDataLoaded(true);
          setNotifications(response);
        })
        .catch(e => handleError(e.response.data)
        )
  }

  const handleChange = async ({values}: NotificationFiledProps) => {
    await searchAllNotifications(values);
  }

  const updateNotifyEmailsProperty = async (id: number, notifyEmails: boolean) => {
    const notifyEmailsString : string = notifyEmails ? "YES" : "NO";
    await updateNotifyEmails(id, notifyEmailsString, cancelTokenSource)
        .then(response => {
          const notificationDtos = notifications.map(notification => {
            return notification.id === id ? response : notification;
          });
          setNotifications(notificationDtos);
        })
        .catch(e => handleError(e.response.data))
  }

  const downloadFile = async (id: number) => {
    await downloadFileFromFileArchiveId(id, cancelTokenSource)
        .then(response => {
          setSuccessMessage(getMessage("testAuthorityNotification.successfullyDownloadedFile"))
        })
        .catch(e => handleError(e.response.data))
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors = [
      errorUtils.canNotGetFile,
      errorUtils.unknownNotification
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };
  const renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage &&
              <Grid.Row>
                <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                  {errorMessage}
                </StyledErrorMessage>
              </Grid.Row>
              }
              <PraxFormGrid>
                {
                    successMessage &&
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message success onDismiss={() => setSuccessMessage(undefined)}>
                        <p>
                          {successMessage}
                        </p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column width={4}>
                    <StyledGrid>
                      <Grid.Row>
                        <Grid.Column width={4}>
                          {getMessage("from")}:
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                              name="fromDate"
                              component={DatePicker}
                              fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4}>
                          {getMessage("to")}:
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                              name="toDate"
                              component={DatePicker}
                              fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4}/>
                        <Grid.Column width={12}>
                          <Field name="alreadyNotified"
                                 component={CheckBox}
                                 label={getMessage("testAuthorityNotification.alreadyNotified")}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </StyledGrid>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>
            <FormButtonsRow>
              <Button
                  type="button"
                  secondary
                  compact
                  onClick={() => generateTestsExportFile(values)}
              >
                {getMessage("testAuthorityNotification.generate")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
          <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState)}/>
        </form>
    )
  };

  const notificationsRowGetter = ({index}: any) => {
    Object.assign(notifications[index], {index: index + 1});
    return notifications[index];
  };

  const notificationsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  const renderTableSegment = (): JSX.Element => {

    return (
        <VirtualizedTable
            rowCount={notifications.length}
            rowGetter={notificationsRowGetter}
            rowRenderer={notificationsRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                label: (getMessage("testAuthorityNotification.reportType")),
                dataKey: "reportType",
                cellRenderer: reportTypeCellRenderer
              },
              {
                width: 300,
                label: (getMessage("testAuthorityNotification.notificationType")),
                dataKey: "notificationType",
                cellRenderer: notificationTypeCellRenderer
              },
              {
                width: 300,
                label: (getMessage("testAuthorityNotification.creationDate")),
                dataKey: "creationDate",
                cellRenderer: dateCellRenderer
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("testAuthorityNotification.notifiedEmails")),
                dataKey: "notifiedEmails",
                cellRenderer: notifyEmailsRenderer
              },
              {
                width: 100,
                flexGrow: 1,
                label: (getMessage("testAuthorityNotification.numberOfRecords")),
                dataKey: "numberOfRecords"
              },
              {
                width: 300,
                label: (getMessage("testAuthorityNotification.fromDate")),
                dataKey: "fromDate",
                cellRenderer: fromToDateCellRenderer
              },
              {
                width: 300,
                label: (getMessage("testAuthorityNotification.toDate")),
                dataKey: "toDate",
                cellRenderer: fromToDateCellRenderer
              },
              {
                width: 150,
                flexGrow: 1,
                label: (getMessage("testAuthorityNotification.download")),
                dataKey: "id",
                cellRenderer: downloadCellRenderer
              }
            ]}
        />

    );
  };

  const dateCellRenderer = (data: any) => {

    const {rowData} = data;
    const creationDate = rowData.creationDate;
    const creationTime = rowData.creationTime;

    return (
        <div>
          {moment(creationDate).format("DD.MM.YYYY") + " " + moment(creationTime).format("HH:mm")}
        </div>
    );
  }

  const fromToDateCellRenderer = (data: any) => {

    const {cellData} = data;
    return (
        <div>
          {cellData !== null
              ? moment(cellData).format("DD.MM.YYYY")
              : ""
          }
        </div>
    );
  }

  const reportTypeCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return getMessage(`enum.NotificationReportType.${cellData}`)
    }
  }

  const notificationTypeCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return getMessage(`enum.NotificationType.${cellData}`)
    }
  }

  const notifyEmailsRenderer = ({rowData, cellData}: any) => {
    const checked = cellData === 'YES';
    return <CheckboxSc checked={checked} onClick={() => updateNotifyEmailsProperty(rowData.id, !checked)}/>
  }

  const downloadCellRenderer = (data: any) => {
    const {rowData} = data;
    if (rowData.id) {
      return <Icon name="download"
                   onClick={() => downloadFile(rowData.id)}
      />
    } else {
      return ""
    }
  }

  return (
      <PraxStyledSegment>
        <FinalForm
            onSubmit={(values) => generateTestsExportFile(values)}
            initialValues={initialValues}
            subscription={{values: true}}
            render={renderForm}
        />
        <StyledViewTableRow>
          {!dataLoaded
              ? <LoaderComponent message={getMessage("loading")}/>
              : renderTableSegment()}
        </StyledViewTableRow>
      </PraxStyledSegment>
  );
}

export default withMessages(withAuthContext(TestAuthorityNotificationView))
